import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heart, MapPin, Bed, Bath, Maximize, Phone, Mail, MessageCircle, Camera, Calendar, LandPlot, Compass, Building2, Share2, Flame, ShieldCheck, Send, BadgeCheck } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useState, useEffect } from 'react';
import { logFeaturedListingEvent } from '@/lib/featuredAnalytics';
import { TierBadge } from './TierBadge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { CallContactDialog } from './CallContactDialog';
import { EmailContactDialog } from './EmailContactDialog';
import { formatDistanceToNow } from 'date-fns';
import { optimizedImageUrl, optimizedSrcSet } from '@/lib/imageOptimizer';
import { useCurrency } from '@/contexts/CurrencyContext';
import { formatPrice as fmtPrice } from '@/lib/formatPrice';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { useAgencySummary } from '@/hooks/useAgencySummary';

interface PropertyCardProps {
  property: any;
  layout?: 'grid' | 'list';
  cardType?: 'property' | 'project' | 'plot' | 'commercial';
}

export function PropertyCard({ property, layout = 'grid', cardType = 'property' }: PropertyCardProps) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [saved, setSaved] = useState(false);
  const [callDialogOpen, setCallDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const isMobile = useIsMobile();

  const lang = i18n.language || 'en';
  const title = typeof property.title === 'object' ? (property.title[lang] || property.title.en || '') : (property.title || property.name || '');
  const description = typeof property.description === 'object' ? (property.description?.[lang] || property.description?.en || '') : (property.description || '');
  const images = cardType === 'plot'
    ? (property.plot_images || [])
    : (property.property_images || property.project_images || []);
  const primaryImageRaw = images.find((img: any) => img.is_primary)?.url
    || images[0]?.url
    || 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=600&h=400&fit=crop';
  // Grid card ~400px wide, list card ~360px wide; request ~480px (covers 2x on small/retail).
  const gridImage = optimizedImageUrl(primaryImageRaw, { width: 480, height: 360, quality: 75 });
  const gridSrcSet = optimizedSrcSet(primaryImageRaw, { width: 480, height: 360, quality: 75 });
  const listImage = optimizedImageUrl(primaryImageRaw, { width: 480, height: 360, quality: 75 });
  const listSrcSet = optimizedSrcSet(primaryImageRaw, { width: 480, height: 360, quality: 75 });
  const imageCount = images.length;
  const agentLogo = property.agent_logo_url || property.agency_logo_url || property.logo_url || null;
  const features = property.features as any;
  const commercialSubType = features?.commercial_sub_type;

  // Featured tier (super_hot > hot > standard) from Ad Ops / paid package
  const tier = property.featured_tier as 'super_hot' | 'hot' | 'standard' | null | undefined;
  const tierContext = (cardType === 'commercial' ? 'property' : cardType) as 'property' | 'project' | 'plot';
  const hasTierBadge = !!tier || !!property.is_featured;

  // Track featured-listing impression once per session/listing when this card has an active boost.
  useEffect(() => {
    if (!tier || !property?.id) return;
    const lt = (cardType === 'commercial' ? 'property' : cardType) as 'property' | 'plot' | 'project';
    logFeaturedListingEvent(property.id, lt, 'impression');
  }, [tier, property?.id, cardType]);

  const trackFeaturedClick = () => {
    if (!tier || !property?.id) return;
    const lt = (cardType === 'commercial' ? 'property' : cardType) as 'property' | 'plot' | 'project';
    logFeaturedListingEvent(property.id, lt, 'click');
  };

  const { displayCurrency, rate } = useCurrency();
  const formatPrice = (price: number, currency: string) =>
    fmtPrice(price, currency, displayCurrency, rate);

  const timeAgo = property.created_at
    ? formatDistanceToNow(new Date(property.created_at), { addSuffix: true })
    : '';

  const handleSave = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast({ variant: 'destructive', title: 'Please login to save properties' });
      return;
    }
    if (saved) {
      await supabase.from('favorites').delete().eq('property_id', property.id).eq('user_id', user.id);
      setSaved(false);
    } else {
      await supabase.from('favorites').insert({ property_id: property.id, user_id: user.id });
      setSaved(true);
    }
  };

  const handleAction = (e: React.MouseEvent, action: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (action === 'call' && property.contact_phone) {
      window.open(`tel:${property.contact_phone}`, '_self');
    } else if (action === 'whatsapp' && property.contact_phone) {
      window.open(`https://wa.me/${property.contact_phone.replace(/[^0-9]/g, '')}`, '_blank');
    } else if (action === 'email' && property.contact_email) {
      window.open(`mailto:${property.contact_email}`, '_self');
    } else {
      toast({ title: 'Contact info not available' });
    }
  };

  const detailLink = cardType === 'project'
    ? `/projects/${property.id}`
    : cardType === 'plot'
    ? `/plot/${property.id}`
    : cardType === 'commercial'
    ? `/commercial/${property.id}`
    : `/property/${property.id}`;

  // ─── MOBILE VARIANT (Zameen-style) ───────────────────────────
  if (isMobile) {
    const isProjectStyle = cardType === 'project' || cardType === 'plot' || cardType === 'commercial';
    const projectChips: string[] = (() => {
      if (cardType === 'project') {
        const ut = property.unit_types;
        if (Array.isArray(ut) && ut.length) return ut.slice(0, 3);
        if (property.property_type) return [property.property_type];
        return [];
      }
      if (cardType === 'plot') return property.plot_purpose ? [property.plot_purpose] : [];
      if (cardType === 'commercial') return commercialSubType ? [commercialSubType] : [];
      return [];
    })();

    const mobilePrice = (() => {
      if (cardType === 'project' && (property.price_min || property.price_max)) {
        const min = property.price_min;
        const max = property.price_max;
        if (min && max && min !== max) {
          return `${formatPrice(min, property.currency || 'ETB')} – ${formatPrice(max, property.currency || 'ETB')}`;
        }
        return formatPrice(min || max, property.currency || 'ETB');
      }
      return formatPrice(property.price, property.currency || 'ETB');
    })();

    if (isProjectStyle) {
      return (
        <>
          <div className="border rounded-xl bg-card overflow-hidden shadow-sm">
            <div className="flex">
              <Link to={detailLink} className="relative w-[140px] shrink-0 overflow-hidden">
                <img src={gridImage} srcSet={gridSrcSet} alt={title} className="w-full h-full object-cover" loading="lazy" decoding="async" />
              </Link>
              <div className="flex-1 min-w-0 p-2.5 flex flex-col">
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="inline-flex items-center text-[10px] font-medium px-2 py-0.5 border border-primary/40 text-primary rounded">
                    Marketed By
                  </span>
                  <BadgeCheck className="h-3.5 w-3.5 text-primary fill-primary/10" />
                </div>
                <Link to={detailLink} className="block" onClick={trackFeaturedClick}>
                  <div className="text-sm font-bold text-foreground leading-tight">{mobilePrice}</div>
                  {description && (
                    <p className="text-[11px] text-muted-foreground line-clamp-1 mt-0.5">{description}</p>
                  )}
                  <h3 className="font-semibold text-sm text-foreground mt-1 line-clamp-1">{title}</h3>
                  <div className="flex items-center gap-1 text-[11px] text-muted-foreground mt-0.5">
                    <MapPin className="h-3 w-3 text-primary shrink-0" />
                    <span className="line-clamp-1">{property.city}{property.sub_city ? `, ${property.sub_city}` : ''}</span>
                  </div>
                </Link>
                {projectChips.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {projectChips.map((c, i) => (
                      <span key={i} className="text-[10px] capitalize px-2 py-0.5 border border-border rounded-full text-foreground/70">{String(c).replace('_', ' ')}</span>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-1.5 mt-2 pt-2 border-t">
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEmailDialogOpen(true); }}
                    className="h-7 w-7 shrink-0 rounded border border-primary/40 text-primary flex items-center justify-center"
                    aria-label="Email"
                  >
                    <Send className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={(e) => handleAction(e, 'whatsapp')}
                    className="h-7 w-7 shrink-0 rounded border border-primary/40 text-primary flex items-center justify-center"
                    aria-label="WhatsApp"
                  >
                    <MessageCircle className="h-3.5 w-3.5" />
                  </button>
                  <Button
                    size="sm"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setCallDialogOpen(true); }}
                    className="flex-1 h-7 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-xs"
                  >
                    <Phone className="h-3 w-3 mr-1" /> Call
                  </Button>
                </div>
              </div>
            </div>
          </div>
          <CallContactDialog
            open={callDialogOpen}
            onOpenChange={setCallDialogOpen}
            agentName={property.contact_person || 'Property Agent'}
            agencyName={property.agency_name || property.developer_name}
            mobileNumber={property.contact_phone}
            phoneNumber={property.contact_phone}
            referenceId={property.reference_code || `ID${property.id?.slice(0, 8)}`}
          />
          <EmailContactDialog
            open={emailDialogOpen}
            onOpenChange={setEmailDialogOpen}
            agentName={property.contact_person || 'Property Agent'}
            agencyName={property.agency_name || property.developer_name}
            agentLogo={agentLogo}
            phoneNumber={property.contact_phone}
            referenceId={property.reference_code || `ID${property.id?.slice(0, 8)}`}
            propertyId={undefined}
            projectId={cardType === 'project' ? property.id : undefined}
          />
        </>
      );
    }

    // Residential mobile card (image 1 style)
    return (
      <>
        <div className="border rounded-xl bg-card overflow-hidden shadow-sm">
          <div className="flex">
            <Link to={detailLink} className="relative w-[125px] shrink-0 overflow-hidden">
              <img src={gridImage} srcSet={gridSrcSet} alt={title} className="w-full h-full object-cover" loading="lazy" decoding="async" />
              {tier && (
                <span className="absolute top-0 left-0 z-10">
                  <TierBadge tier={tier} context={tierContext} size="sm" className="rounded-none rounded-br" />
                </span>
              )}
              {imageCount > 1 && (
                <div className="absolute bottom-1.5 left-1.5 flex items-center gap-1 bg-black/70 text-white text-[9px] px-1.5 py-0.5 rounded">
                  <Camera className="h-2.5 w-2.5" />{imageCount}
                </div>
              )}
            </Link>
            <div className="flex-1 min-w-0 p-2.5 flex flex-col">
              <div className="flex items-center justify-between gap-2 mb-0.5">
                <span className="text-[10px] text-muted-foreground">{timeAgo}</span>
                {tier && <TierBadge tier={tier} context={tierContext} size="sm" />}
              </div>
              <Link to={detailLink} className="block" onClick={trackFeaturedClick}>
                <div className="flex items-baseline gap-1">
                  <span className="text-[10px] font-medium text-muted-foreground uppercase">{property.currency || 'ETB'}</span>
                  <span className="text-sm font-bold text-foreground">{mobilePrice.replace(/^[A-Za-z$]+\s*/, '')}</span>
                </div>
                <h3 className="font-semibold text-xs text-foreground line-clamp-1 mt-0.5">{title}</h3>
                <div className="text-[11px] text-muted-foreground capitalize">
                  {property.property_type || 'Property'} {property.listing_type === 'rent' ? 'for Rent' : 'for Sale'}
                </div>
                <div className="flex gap-2.5 text-[11px] text-foreground/80 mt-1">
                  {property.bedrooms != null && <span className="flex items-center gap-0.5"><Bed className="h-3 w-3" />{property.bedrooms}</span>}
                  {property.bathrooms != null && <span className="flex items-center gap-0.5"><Bath className="h-3 w-3" />{property.bathrooms}</span>}
                  {property.area_sqm != null && <span className="flex items-center gap-0.5"><Maximize className="h-3 w-3" />{property.area_sqm}m²</span>}
                </div>
              </Link>
              <div className="flex items-center gap-1.5 mt-2 pt-2 border-t">
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEmailDialogOpen(true); }}
                  className="flex-1 h-7 rounded border border-primary/50 text-primary text-[11px] font-semibold inline-flex items-center justify-center gap-1"
                >
                  <Mail className="h-3 w-3" /> SMS
                </button>
                <Button
                  size="sm"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setCallDialogOpen(true); }}
                  className="flex-1 h-7 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-[11px]"
                >
                  <Phone className="h-3 w-3 mr-1" /> CALL
                </Button>
                <button
                  onClick={(e) => handleAction(e, 'whatsapp')}
                  className="h-7 w-7 shrink-0 rounded-full bg-primary text-primary-foreground flex items-center justify-center"
                  aria-label="WhatsApp"
                >
                  <MessageCircle className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={handleSave}
                  className="h-7 w-7 shrink-0 flex items-center justify-center"
                  aria-label="Save"
                >
                  <Heart className={`h-3.5 w-3.5 ${saved ? 'fill-red-500 text-red-500' : 'text-muted-foreground'}`} />
                </button>
              </div>
            </div>
          </div>
        </div>
        <CallContactDialog
          open={callDialogOpen}
          onOpenChange={setCallDialogOpen}
          agentName={property.contact_person || 'Property Agent'}
          agencyName={property.agency_name || property.developer_name}
          mobileNumber={property.contact_phone}
          phoneNumber={property.contact_phone}
          referenceId={property.reference_code || `ID${property.id?.slice(0, 8)}`}
        />
        <EmailContactDialog
          open={emailDialogOpen}
          onOpenChange={setEmailDialogOpen}
          agentName={property.contact_person || 'Property Agent'}
          agencyName={property.agency_name || property.developer_name}
          agentLogo={agentLogo}
          phoneNumber={property.contact_phone}
          referenceId={property.reference_code || `ID${property.id?.slice(0, 8)}`}
          propertyId={cardType === 'property' ? property.id : undefined}
        />
      </>
    );
  }

  // ─── GRID CARD ───────────────────────────────────────────────
  if (layout === 'grid') {
    return (
      <Link to={detailLink} onClick={trackFeaturedClick} className="group border rounded-xl bg-card overflow-hidden hover:shadow-md transition-all">
        <div className="relative aspect-[4/3] overflow-hidden">
          <img src={gridImage} srcSet={gridSrcSet} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" decoding="async" />
          <Badge className="absolute top-2.5 left-2.5 text-[10px] font-semibold">
            {cardType === 'project' ? (property.status?.replace('_', ' ') || 'Project') : (property.listing_type === 'sale' ? t('property.forSale') : t('property.forRent'))}
          </Badge>
          {commercialSubType && cardType === 'commercial' && <Badge variant="secondary" className="absolute top-2.5 right-10 text-[10px] capitalize">{commercialSubType}</Badge>}
          {hasTierBadge && (
            <div className="absolute top-2.5 right-10 flex flex-col items-end gap-0.5">
              <TierBadge tier={tier} context={tierContext} isFeaturedFallback={!!property.is_featured} />
              {tier && <span className="text-[8px] uppercase tracking-wider bg-black/60 text-white px-1.5 py-0.5 rounded">Sponsored</span>}
            </div>
          )}
          <Button variant="ghost" size="icon" className="absolute top-2 right-2 h-7 w-7 bg-white/80 hover:bg-white rounded-full" onClick={handleSave}>
            <Heart className={`h-3.5 w-3.5 ${saved ? 'fill-red-500 text-red-500' : ''}`} />
          </Button>
          {imageCount > 1 && (
            <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded">
              <Camera className="h-3 w-3" />{imageCount}
            </div>
          )}
        </div>
        <div className="p-3.5">
          <div className="flex items-baseline gap-1">
            <span className="font-display text-base font-bold text-primary">
              {cardType === 'project' && property.price_min
                ? `${formatPrice(property.price_min, property.currency || 'ETB')} - ${formatPrice(property.price_max || property.price_min, property.currency || 'ETB')}`
                : formatPrice(property.price, property.currency || 'ETB')}
            </span>
            {property.listing_type === 'rent' && <span className="text-xs text-muted-foreground">/mo</span>}
          </div>
          <h3 className="font-medium text-sm mt-1 line-clamp-1 group-hover:text-primary transition-colors">{title}</h3>
          <div className="flex items-center gap-1 text-muted-foreground text-xs mt-1">
            <MapPin className="h-3 w-3 shrink-0" />
            <span className="line-clamp-1">{property.city}{property.sub_city ? `, ${property.sub_city}` : ''}</span>
          </div>
          <div className="flex gap-3 mt-3 pt-2.5 border-t text-xs text-muted-foreground">
            {cardType === 'plot' ? (
              <>
                {property.area_sqm != null && <span className="flex items-center gap-1"><LandPlot className="h-3.5 w-3.5" />{property.area_sqm}m²</span>}
                {property.plot_purpose && <span className="capitalize">{property.plot_purpose}</span>}
                {property.facing_direction && <span className="flex items-center gap-1"><Compass className="h-3.5 w-3.5" />{property.facing_direction.replace('_', ' ')}</span>}
              </>
            ) : cardType === 'commercial' ? (
              <>
                {property.area_sqm != null && <span className="flex items-center gap-1"><Maximize className="h-3.5 w-3.5" />{property.area_sqm}m²</span>}
                {property.total_floors != null && <span className="flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{property.total_floors} floors</span>}
              </>
            ) : (
              <>
                {property.bedrooms != null && <span className="flex items-center gap-1"><Bed className="h-3.5 w-3.5" />{property.bedrooms}</span>}
                {property.bathrooms != null && <span className="flex items-center gap-1"><Bath className="h-3.5 w-3.5" />{property.bathrooms}</span>}
                {property.area_sqm != null && <span className="flex items-center gap-1"><Maximize className="h-3.5 w-3.5" />{property.area_sqm}m²</span>}
              </>
            )}
          </div>
          {cardType === 'plot' && (
            <div className="flex gap-1.5 flex-wrap mt-1">
              {property.is_corner && <Badge variant="outline" className="text-[10px] px-1.5 py-0">Corner</Badge>}
              {property.is_park_face && <Badge variant="outline" className="text-[10px] px-1.5 py-0">Park Face</Badge>}
              {property.is_main_road && <Badge variant="outline" className="text-[10px] px-1.5 py-0">Main Road</Badge>}
            </div>
          )}
        </div>
      </Link>
    );
  }

  // ─── LIST CARD (Zameen-style) ────────────────────────────────
  return (
    <>
    <div className="group border rounded-xl bg-card overflow-hidden hover:shadow-lg transition-all">
      <div className="flex flex-col sm:flex-row">
        {/* Image */}
        <Link to={detailLink} className="relative w-full sm:w-[260px] md:w-[290px] aspect-[4/3] sm:aspect-auto sm:h-auto shrink-0 overflow-hidden">
          <img src={listImage} srcSet={listSrcSet} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" decoding="async" />
          {hasTierBadge && (
            <span className="absolute top-0 left-0 z-10">
              <TierBadge tier={tier} context={tierContext} isFeaturedFallback={!!property.is_featured} className="rounded-none rounded-br-md px-2.5 py-1 text-[11px]" />
            </span>
          )}
          {imageCount > 1 && (
            <div className="absolute bottom-2.5 left-2.5 flex items-center gap-1 bg-black/70 text-white text-xs px-2 py-1 rounded">
              <Camera className="h-3.5 w-3.5" />{imageCount}
            </div>
          )}
          <div className="absolute bottom-2.5 right-2.5 flex items-center gap-1.5">
            <button onClick={(e) => { e.preventDefault(); }} className="h-7 w-7 rounded-full bg-white/90 hover:bg-white flex items-center justify-center transition-colors" aria-label="Map">
              <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
            <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); }} className="h-7 w-7 rounded-full bg-white/90 hover:bg-white flex items-center justify-center transition-colors" aria-label="Share">
              <Share2 className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
            <button onClick={handleSave} className="h-7 w-7 rounded-full bg-white/90 hover:bg-white flex items-center justify-center transition-colors" aria-label="Save">
              <Heart className={`h-3.5 w-3.5 ${saved ? 'fill-red-500 text-red-500' : 'text-muted-foreground'}`} />
            </button>
          </div>
        </Link>

        {/* Content */}
        <div className="flex-1 flex flex-col p-2.5 min-w-0">
          {/* Top status bar: hot/verified left, titanium right */}
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-1.5">
              {tier && <Flame className="h-3.5 w-3.5 text-orange-500 fill-orange-500" />}
              <ShieldCheck className="h-3.5 w-3.5 text-primary" />
            </div>
            {tier && <TierBadge tier={tier} context={tierContext} size="sm" />}
          </div>

          <Link to={detailLink} onClick={trackFeaturedClick} className="flex-1 min-w-0">
            {/* Price */}
            <div className="flex items-baseline gap-1.5 mb-0.5">
              {cardType === 'project' && <span className="text-xs text-muted-foreground">Starting from</span>}
              <span className="text-[11px] font-semibold text-muted-foreground uppercase">{property.currency || 'ETB'}</span>
              <span className="font-display text-xl font-bold text-foreground leading-none">
                {(() => {
                  const price = cardType === 'project' && property.price_min ? property.price_min : property.price;
                  const formatted = formatPrice(price, property.currency || 'ETB');
                  return formatted.replace(/^[A-Z]{3}\s*/, '');
                })()}
              </span>
              {property.listing_type === 'rent' && <span className="text-xs text-muted-foreground">/mo</span>}
            </div>

            {/* Location */}
            <div className="text-xs text-foreground/80 mb-1 line-clamp-1">
              {property.city}{property.sub_city ? ` - ${property.sub_city}` : ''}{property.woreda ? `, ${property.woreda}` : ''}
            </div>

            {/* Specs row (compact) */}
            <div className="flex gap-3 text-xs text-foreground/80 mb-1">
              {cardType === 'plot' ? (
                <>
                  {property.area_sqm != null && <span className="flex items-center gap-1"><LandPlot className="h-3.5 w-3.5" />{property.area_sqm} m²</span>}
                  {property.plot_purpose && <span className="capitalize">{property.plot_purpose}</span>}
                  {property.facing_direction && <span className="flex items-center gap-1"><Compass className="h-3.5 w-3.5" />{property.facing_direction.replace('_', ' ')}</span>}
                </>
              ) : cardType === 'commercial' ? (
                <>
                  {property.area_sqm != null && <span className="flex items-center gap-1"><Maximize className="h-3.5 w-3.5" />{property.area_sqm} m²</span>}
                  {property.total_floors != null && <span className="flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{property.total_floors}</span>}
                </>
              ) : (
                <>
                  {property.bedrooms != null && <span className="flex items-center gap-1"><Bed className="h-3.5 w-3.5" />{property.bedrooms}</span>}
                  {property.bathrooms != null && <span className="flex items-center gap-1"><Bath className="h-3.5 w-3.5" />{property.bathrooms}</span>}
                  {property.area_sqm != null && <span className="flex items-center gap-1"><Maximize className="h-3.5 w-3.5" />{property.area_sqm} Sq. M.</span>}
                </>
              )}
            </div>

            {/* Title */}
            <h3 className="font-semibold text-sm text-foreground group-hover:text-primary transition-colors line-clamp-1 mb-0.5">{title}</h3>

            {/* Description preview + inline timestamp */}
            {(description || timeAgo) && (
              <p className="text-xs text-muted-foreground line-clamp-1 mb-1">
                {property.agency_name && <span className="font-semibold text-foreground/80 uppercase">{property.agency_name} </span>}
                {description}
                {description && <span className="text-primary mx-1">more</span>}
                {timeAgo && <span className="text-muted-foreground/70">· {timeAgo}</span>}
              </p>
            )}
          </Link>

          {/* Action buttons + Agent/Agency logo */}
          <div className="flex items-center justify-between pt-2 border-t mt-auto">
            <div className="flex items-center gap-1.5">
              {cardType === 'project' ? (
                <>
                  <Button size="sm" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-xs h-7 px-2.5" asChild>
                    <Link to={detailLink} onClick={(e) => e.stopPropagation()}>Reserve</Link>
                  </Button>
                  <Button size="sm" variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground font-semibold text-xs h-7 px-2.5" onClick={(e) => handleAction(e, 'call')}>
                    <Phone className="h-3 w-3 mr-1" />Call
                  </Button>
                  <Button size="sm" variant="outline" className="font-semibold text-xs h-7 px-2.5" onClick={(e) => handleAction(e, 'email')}>
                    <Mail className="h-3 w-3 mr-1" />Email
                  </Button>
                </>
              ) : (
                <>
                  <Button size="icon" variant="outline" className="h-7 w-7 shrink-0 border-primary/50 text-primary hover:bg-primary hover:text-primary-foreground" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEmailDialogOpen(true); }}>
                    <Mail className="h-3 w-3" />
                  </Button>
                  <Button size="sm" variant="outline" className="border-primary/50 text-primary hover:bg-primary hover:text-primary-foreground font-semibold text-xs h-7 px-2.5" onClick={(e) => handleAction(e, 'whatsapp')}>
                    <MessageCircle className="h-3 w-3 mr-1" />WhatsApp
                  </Button>
                  <Button size="sm" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-xs h-7 px-2.5" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setCallDialogOpen(true); }}>
                    <Phone className="h-3 w-3 mr-1" />CALL
                  </Button>
                </>
              )}
            </div>

            {/* Agent/Agency logo with hover card */}
            {agentLogo && (
              <AgencyLogoHover
                agencyId={property.agency_id}
                fallbackName={property.agency_name}
                fallbackLogo={agentLogo}
                fallbackRegisteredAt={property.agency_registered_at}
                slug={property.agency_slug}
              />
            )}
          </div>
        </div>
      </div>
    </div>

    {/* Contact Dialogs */}
    <CallContactDialog
      open={callDialogOpen}
      onOpenChange={setCallDialogOpen}
      agentName={property.contact_person || 'Property Agent'}
      agencyName={property.agency_name || property.developer_name}
      mobileNumber={property.contact_phone}
      phoneNumber={property.contact_phone}
      referenceId={property.reference_code || `ID${property.id?.slice(0, 8)}`}
    />
    <EmailContactDialog
      open={emailDialogOpen}
      onOpenChange={setEmailDialogOpen}
      agentName={property.contact_person || 'Property Agent'}
      agencyName={property.agency_name || property.developer_name}
      agentLogo={agentLogo}
      phoneNumber={property.contact_phone}
      referenceId={property.reference_code || `ID${property.id?.slice(0, 8)}`}
      propertyId={cardType === 'property' ? property.id : undefined}
      projectId={cardType === 'project' ? property.id : undefined}
    />
    </>
  );
}

interface AgencyLogoHoverProps {
  agencyId?: string | null;
  fallbackName?: string | null;
  fallbackLogo?: string | null;
  fallbackRegisteredAt?: string | null;
  slug?: string | null;
}

function AgencyLogoHover({ agencyId, fallbackName, fallbackLogo, fallbackRegisteredAt, slug }: AgencyLogoHoverProps) {
  const [open, setOpen] = useState(false);
  const { data } = useAgencySummary(open ? agencyId || undefined : undefined);

  const name = data?.name || fallbackName;
  const logo = data?.logoUrl || fallbackLogo;
  const registered = data?.registeredAt || fallbackRegisteredAt;
  const year = registered ? new Date(registered).getFullYear() : null;
  const agencyHref = slug ? `/agencies/${slug}` : agencyId ? `/agencies/${agencyId}` : null;

  return (
    <HoverCard openDelay={120} closeDelay={80} onOpenChange={setOpen}>
      <HoverCardTrigger asChild>
        <Link
          to={agencyHref || '#'}
          onClick={(e) => { if (!agencyHref) e.preventDefault(); e.stopPropagation(); }}
          className="shrink-0 ml-3"
        >
          <img
            src={fallbackLogo || ''}
            alt={fallbackName || 'Agency'}
            className="h-8 w-auto max-w-[70px] object-contain rounded opacity-80 hover:opacity-100 transition-opacity"
          />
        </Link>
      </HoverCardTrigger>
      <HoverCardContent side="top" align="end" className="w-64 p-4">
        <div className="flex flex-col items-center text-center">
          {logo && (
            <img src={logo} alt={name || 'Agency'} className="h-14 w-auto max-w-[120px] object-contain mb-2" />
          )}
          {name && <div className="font-bold text-sm uppercase tracking-wide text-foreground">{name}</div>}
          {year && (
            <div className="text-xs text-muted-foreground mt-1">Registered since: {year}</div>
          )}
          {data && (
            <div className="text-xs text-foreground/80 mt-2 space-y-0.5">
              <div>{data.salesCount} Properties for Sale</div>
              <div>{data.rentCount} Properties for Rent</div>
            </div>
          )}
          {agencyHref && (
            <Link
              to={agencyHref}
              onClick={(e) => e.stopPropagation()}
              className="text-xs font-semibold text-primary mt-3 hover:underline"
            >
              View agency →
            </Link>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

